// src/views/balances/actions/components/ContractActionsToolbar.js
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Typography, Button } from '@mui/material';
import Breadcrumb from 'src/components/Breadcrumb';
import { useDispatch, useSelector } from 'react-redux';
import { FilterList, GetApp } from '@mui/icons-material';
import dayjs from 'src/utils/dayjs';
import Filter from 'src/components/Filter/Filter';
import FILTER_TYPES from 'src/utils/constants/filter';
import {
  loadContractActions,
  downloadExcelContractActions,
  CLEAR_CONTRACT_ACTIONS
} from 'src/store/actions/contractActionsActions';
import { selectContractActionsQueryOptions } from 'src/store/selectors/contractActionsSelectors';
import useStyles from '../../capitalBalance/components/styles';

const filters = [
  {
    title: 'COMMON.DATE',
    controlGroup: FILTER_TYPES.DATE,
    items: [
      {
        label: 'COMMON.DATE',
        value: 'date',
        view: ['year', 'month']
      }
    ]
  }
];

function getFilterOptions(options) {
  let month = null;
  let year = null;
  if (options.date) {
    month = options.date.month() + 1;
    year = options.date.year();
  }
  return { year, month };
}

const ContractActionsToolbar = ({ className, title, ...rest }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const queryOptions = useSelector(selectContractActionsQueryOptions);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterValues, setFilterValues] = useState({ date: dayjs.tz() });

  useEffect(() => {
    const now = dayjs.tz();
    dispatch(
      loadContractActions({ month: now.month() + 1, year: now.year() })
    );
    return () => dispatch({ type: CLEAR_CONTRACT_ACTIONS });
  }, [dispatch]);

  const handleClickFilter = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseFilter = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleFilterChange = useCallback(
    event => {
      setFilterValues(event);
      const filterOpts = getFilterOptions(event);
      dispatch(
        loadContractActions({ ...queryOptions, ...filterOpts, page: 0 })
      );
    },
    [dispatch, queryOptions]
  );

  const handleExcelExport = useCallback(() => {
    dispatch(downloadExcelContractActions());
  }, [dispatch]);

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Breadcrumb />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}
      >
        <div>
          <Typography style={{ fontWeight: '400' }} color="primary" variant="h1">
            {title}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('CONTRACT_ACTIONS.PAGE_SUBTITLE')}
          </Typography>
        </div>
        <div className={classes.actionsContainer}>
          <Filter
            anchorEl={anchorEl}
            filterValues={filterValues}
            handleClose={handleCloseFilter}
            handleChange={handleFilterChange}
            filters={filters}
          />
          <Button
            className={clsx(
              classes.buttons,
              classes.outlined,
              classes.horizontalPadding
            )}
            color="primary"
            onClick={handleClickFilter}
          >
            <FilterList className={classes.icon} />
            {t('COMMON.FILTER')}
          </Button>
          <Button
            className={clsx(classes.buttons, classes.outlined)}
            onClick={handleExcelExport}
            color="primary"
          >
            <GetApp className={classes.icon} />
          </Button>
        </div>
      </div>
    </div>
  );
};

ContractActionsToolbar.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string.isRequired
};

ContractActionsToolbar.defaultProps = {
  className: undefined
};

export default ContractActionsToolbar;
